import React from 'react';
import { motion } from 'motion/react';
import { getActiveRecipe } from '@/lib/utils';
import { ART_STYLE } from '@/lib/VisualSystem';

interface BackgroundAnimationProps {
  opacity?: number;
}

export const BackgroundAnimation: React.FC<BackgroundAnimationProps> = ({ opacity = 1 }) => {
  const recipe = getActiveRecipe();
  const isPristine = ART_STYLE.NAME === 'PRISTINE_LIGHT';

  return (
    <div className={`fixed inset-0 -z-20 pointer-events-none overflow-hidden ${recipe.bg}`} style={{ opacity }}>
      {!isPristine && (
        <>
          {/* Primary Glows */}
          <motion.div
            animate={{
              scale: [1, 1.4, 1.2, 1.5, 1],
              x: [0, 200, 100, -50, 0],
              y: [0, 150, -100, 50, 0],
              rotate: [0, 90, 180, 270, 360],
            }}
            transition={{
              duration: 30,
              repeat: Infinity,
              ease: "linear"
            }}
            className="absolute top-[-15%] left-[-15%] w-[80%] h-[80%] rounded-full bg-[#00e5ff]/25 blur-[140px]"
          />
          
          <motion.div
            animate={{
              scale: [1.3, 1, 1.5, 1.2, 1.3],
              x: [0, -200, 100, -300, 0],
              y: [0, -150, 200, -100, 0],
              rotate: [0, -90, -180, -270, -360],
            }}
            transition={{
              duration: 35,
              repeat: Infinity,
              ease: "linear"
            }}
            className="absolute bottom-[-15%] right-[-15%] w-[80%] h-[80%] rounded-full bg-[#d500f9]/25 blur-[140px]"
          />

          <motion.div
            animate={{
              scale: [1, 1.6, 1.3, 1.7, 1],
              opacity: [0.3, 0.6, 0.4, 0.5, 0.3],
              x: [-100, 100, 200, -200, -100],
              y: [50, -50, 100, -100, 50],
            }}
            transition={{
              duration: 25,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute top-[20%] right-[-10%] w-[60%] h-[60%] rounded-full bg-[#ffea00]/15 blur-[160px]"
          />
        </>
      )}

      {/* Noise Texture Overlay */}
      <div className="absolute inset-0 w-full h-full opacity-[0.03] pointer-events-none mix-blend-overlay noise-bg" />

      {/* Grid Overlay - subtle for white style */}
      <div className={`absolute inset-0 w-full h-full ${isPristine ? 'opacity-[0.03]' : 'opacity-[0.07]'} bg-[radial-gradient(#000_1px,transparent_1px)] [background-size:40px_40px]`} />
      
      {/* Animated lines - hidden for pristine style for maximum "white" effect */}
      {!isPristine && (
        <div className="absolute inset-0 w-full h-full opacity-[0.03]">
          <div className="absolute top-1/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#00e5ff] to-transparent animate-pulse" />
          <div className="absolute top-2/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#d500f9] to-transparent animate-pulse delay-700" />
          <div className="absolute top-3/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#ffea00] to-transparent animate-pulse delay-1000" />
        </div>
      )}
    </div>
  );
};
